// Phase 4.5 — singleton socket.io client.
//
// Connects to the backend with the current access token in
// `auth.token`; reconnects automatically and re-issues `room:join` for
// every previously-joined room (the room set is tracked here so a
// disconnect-reconnect doesn't drop subscriptions).
//
// onAccessTokenChange (from AuthContextV2) drives reconnect/swap when
// the token rotates after a refresh. logout clears the socket.

import { io as makeSocket } from 'socket.io-client';
import { getAccessToken, onAccessTokenChange } from '../contexts/AuthContextV2';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

let socket = null;
const joinedRooms = new Set();
const eventListeners = new Map(); // room -> Map(event -> Set(handler))

function ensureSocket() {
  if (socket) return socket;
  const token = getAccessToken();
  socket = makeSocket(API_BASE, {
    path: '/api/socket.io/',
    autoConnect: true,
    transports: ['websocket', 'polling'],
    auth: token ? { token } : undefined,
    withCredentials: true
  });
  socket.on('connect', () => {
    // Re-join every previously requested room so the server side picks
    // them up on the new socket id.
    for (const room of joinedRooms) {
      socket.emit('room:join', room, () => {});
    }
  });
  socket.on('connect_error', (err) => {
    // Bubble auth errors so the caller can re-auth; non-auth errors are
    // handled by the built-in reconnect.
    if (err?.message === 'Authentication required' || err?.message === 'Authentication failed') {
      // eslint-disable-next-line no-console
      console.warn('[socket] auth error; will retry on next token rotation');
    }
  });
  return socket;
}

// Tear down + recreate when the access token changes (post-refresh).
onAccessTokenChange((token) => {
  if (!socket) return;
  if (!token) {
    socket.disconnect();
    socket = null;
    joinedRooms.clear();
    eventListeners.clear();
    return;
  }
  // Update auth and reconnect so the server picks up the fresh JWT.
  socket.auth = { token };
  socket.disconnect().connect();
});

export function joinRoom(room) {
  const s = ensureSocket();
  joinedRooms.add(room);
  return new Promise((resolve) => {
    s.emit('room:join', room, (ack) => resolve(ack));
  });
}

export function leaveRoom(room) {
  joinedRooms.delete(room);
  if (!socket) return;
  socket.emit('room:leave', room, () => {});
}

export function on(event, handler) {
  const s = ensureSocket();
  s.on(event, handler);
  return () => s.off(event, handler);
}

export function isConnected() {
  return !!(socket && socket.connected);
}

export const socketClient = { joinRoom, leaveRoom, on, isConnected };

export default socketClient;
